import { card, rankValues } from './cards.js';
import type {
  Card,
  CardId,
  Group,
  MeldClassification,
  MeldContext,
  Suit,
} from './types.js';

interface Partitioned {
  /** Cards that can only ever play at face identity. */
  strict: Card[];
  /** Wild-rank cards, which may play as themselves *or* as a wild (§3.1). */
  flex: Card[];
  /** Printed jokers, plus wild-rank cards when the variant forbids natural play. */
  forcedWilds: number;
}

function partition(ids: readonly CardId[], ctx: MeldContext): Partitioned {
  const strict: Card[] = [];
  const flex: Card[] = [];
  let forcedWilds = 0;
  for (const id of ids) {
    const c = card(id);
    if (c.printedJoker) {
      forcedWilds++;
    } else if (ctx.wildRank !== null && c.rank === ctx.wildRank) {
      if (ctx.config.wildCardsMayPlayNatural) flex.push(c);
      else forcedWilds++;
    } else {
      strict.push(c);
    }
  }
  return { strict, flex, forcedWilds };
}

/**
 * Sequence classification.
 *
 * A sequence of length `n` occupies exactly `n` consecutive positions, so the
 * candidate windows are `[a, a+n-1]` for each legal `a` — at most a dozen. Ace
 * is dual-valued and resolves against the window, which is precisely why
 * round-the-corner (K-A-2) cannot be constructed: those values are not
 * contiguous under any single assignment.
 *
 * Placement is greedy and provably optimal: a wild-rank card placed at its own
 * value never costs anything, because the alternative is that it fills some
 * other slot as a wild, and wilds are interchangeable.
 */
export function classifySequence(
  ids: readonly CardId[],
  ctx: MeldContext,
): MeldClassification | null {
  const cfg = ctx.config;
  const n = ids.length;
  if (n < cfg.minMeldSize || n > 13) return null;

  const { strict, flex, forcedWilds } = partition(ids, ctx);

  let suitsToTry: Suit[];
  if (strict.length > 0) {
    const suit = strict[0]!.suit!;
    for (const c of strict) if (c.suit !== suit) return null;
    suitsToTry = [suit];
  } else if (flex.length > 0) {
    suitsToTry = [...new Set(flex.map((c) => c.suit!))];
  } else {
    // Every card is a printed joker.
    return cfg.allowAllWildMeld ? { kind: 'impure-sequence', wildsUsed: n } : null;
  }

  const minValue = cfg.aceLow ? 1 : 2;
  const maxValue = cfg.aceHigh ? 14 : 13;

  let best: MeldClassification | null = null;

  for (const suit of suitsToTry) {
    for (let a = minValue; a + n - 1 <= maxValue; a++) {
      const slots: boolean[] = new Array(n).fill(false);
      let placed = 0;
      let feasible = true;

      for (const c of strict) {
        let seated = false;
        for (const v of rankValues(c.rank!, cfg)) {
          const slot = v - a;
          if (slot < 0 || slot >= n || slots[slot]) continue;
          slots[slot] = true;
          placed++;
          seated = true;
          break;
        }
        if (!seated) {
          feasible = false;
          break;
        }
      }
      if (!feasible) continue;

      for (const c of flex) {
        if (c.suit !== suit) continue;
        for (const v of rankValues(c.rank!, cfg)) {
          const slot = v - a;
          if (slot < 0 || slot >= n || slots[slot]) continue;
          slots[slot] = true;
          placed++;
          break;
        }
      }

      const wildsUsed = n - placed;
      // Sanity: wilds available must cover the gaps. This holds by
      // construction — every unplaced card is playing as a wild.
      if (wildsUsed > flex.length + forcedWilds) continue;

      if (wildsUsed === 0) return { kind: 'pure-sequence', wildsUsed: 0 };
      if (best === null || wildsUsed < best.wildsUsed) {
        best = { kind: 'impure-sequence', wildsUsed };
      }
    }
  }

  if (best === null && cfg.allowAllWildMeld && strict.length === 0) {
    return { kind: 'impure-sequence', wildsUsed: n };
  }
  return best;
}

/**
 * Set classification: three or four cards of one rank in *different* suits
 * (§3.2). Two identical cards — same rank, same suit, one from each deck — is
 * invalid, which falls out of the distinct-suit rule.
 */
export function classifySet(
  ids: readonly CardId[],
  ctx: MeldContext,
): MeldClassification | null {
  const cfg = ctx.config;
  const n = ids.length;
  if (n < cfg.minMeldSize || n > cfg.maxSetSize) return null;

  const { strict, flex, forcedWilds } = partition(ids, ctx);

  if (strict.length === 0 && flex.length === 0) {
    return cfg.allowAllWildMeld ? { kind: 'set', wildsUsed: n } : null;
  }

  const usedSuits = new Set<Suit>();
  let placed = 0;

  if (strict.length > 0) {
    const rank = strict[0]!.rank;
    for (const c of strict) {
      if (c.rank !== rank) return null;
      if (usedSuits.has(c.suit!)) return null;
      usedSuits.add(c.suit!);
      placed++;
    }
    // Wild-rank cards cannot share this rank (they were excluded from `strict`),
    // so they can only play as wilds here.
  } else {
    // Rank of the set is the wild rank itself; wild-rank cards may sit at face
    // identity, and any duplicate suit falls back to playing as a wild.
    for (const c of flex) {
      if (usedSuits.has(c.suit!)) continue;
      usedSuits.add(c.suit!);
      placed++;
    }
    if (placed === 0 && !cfg.allowAllWildMeld) return null;
  }

  const wildsUsed = n - placed;
  if (wildsUsed > flex.length + forcedWilds) return null;
  if (wildsUsed === n && !cfg.allowAllWildMeld) return null;
  return { kind: 'set', wildsUsed };
}

/**
 * Best classification of a group. A sequence is preferred over a set when both
 * apply (only possible for an all-wild group), because sequences are what the
 * winning condition is short of.
 */
export function classifyMeld(
  ids: readonly CardId[],
  ctx: MeldContext,
): MeldClassification | null {
  const seq = classifySequence(ids, ctx);
  if (seq !== null && seq.kind === 'pure-sequence') return seq;
  const set = classifySet(ids, ctx);
  if (seq === null) return set;
  if (set === null) return seq;
  return seq;
}

export function isSequence(kind: MeldKindLike): boolean {
  return kind === 'pure-sequence' || kind === 'impure-sequence';
}

type MeldKindLike = MeldClassification['kind'];

export interface GroupingReport {
  valid: boolean;
  /** Per-group classification, index-aligned with the submitted groups. */
  melds: (MeldClassification | null)[];
  sequences: number;
  pureSequences: number;
  /** Cards that are in no valid meld. */
  ungrouped: CardId[];
  reason?:
    | 'card-count'
    | 'duplicate-card'
    | 'unknown-card'
    | 'invalid-group'
    | 'insufficient-sequences'
    | 'no-pure-sequence';
}

/**
 * Validates a submitted grouping against the hand it claims to arrange.
 * This is the function the server runs on declaration (§3.5); the client runs
 * the same code purely to draw the live underlines (§6.3), and its opinion is
 * never authoritative.
 */
export function evaluateGrouping(
  groups: readonly Group[],
  hand: readonly CardId[],
  ctx: MeldContext,
): GroupingReport {
  const melds: (MeldClassification | null)[] = [];
  const ungrouped: CardId[] = [];
  const seen = new Set<CardId>();
  const handSet = new Set(hand);

  let sequences = 0;
  let pureSequences = 0;
  let reason: GroupingReport['reason'];

  for (const group of groups) {
    for (const id of group) {
      if (seen.has(id)) reason ??= 'duplicate-card';
      seen.add(id);
      if (!handSet.has(id)) reason ??= 'unknown-card';
    }
    const meld = group.length >= ctx.config.minMeldSize ? classifyMeld(group, ctx) : null;
    melds.push(meld);
    if (meld === null) {
      ungrouped.push(...group);
    } else if (meld.kind === 'pure-sequence') {
      sequences++;
      pureSequences++;
    } else if (meld.kind === 'impure-sequence') {
      sequences++;
    }
  }

  if (seen.size !== hand.length) reason ??= 'card-count';
  if (ungrouped.length > 0) reason ??= 'invalid-group';
  if (pureSequences < ctx.config.minPureSequences) reason ??= 'no-pure-sequence';
  if (sequences < ctx.config.minSequences) reason ??= 'insufficient-sequences';

  return {
    valid: reason === undefined,
    melds,
    sequences,
    pureSequences,
    ungrouped,
    ...(reason ? { reason } : {}),
  };
}
